/**
 * PresentationStudio — decks from material FWM already holds.
 *
 * Pick the source (an assigned lesson, an event, or your own notes), pick the
 * FWM template, generate, read what came back, edit it, download the
 * PowerPoint. The gap list is shown as prominently as the slides: a lecturer
 * needs to know when the deck is thin because the lesson is thin.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Presentation, Download, Loader2, AlertTriangle, Sparkles, FileText, Trash2, Save,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCES = [
  { key: 'lesson', label: 'A Bible College lesson' },
  { key: 'event', label: 'An event or conference' },
  { key: 'free', label: 'My own notes' },
];

const SlidePreview = ({ slide, onChange }) => (
  <div className="rounded-lg border bg-white p-4" data-testid={`studio-slide-${slide.index}`}>
    <div className="mb-2 flex items-center gap-2">
      <Badge variant="outline" className="text-[10px] uppercase">{slide.kind}</Badge>
      <span className="text-[11px] text-slate-400">Slide {slide.index}</span>
    </div>
    <Input
      value={slide.title}
      onChange={(e) => onChange({ ...slide, title: e.target.value })}
      className="mb-2 font-semibold"
      data-testid={`studio-slide-title-${slide.index}`}
    />
    <Textarea
      rows={Math.max(2, (slide.points || []).length)}
      value={(slide.points || []).join('\n')}
      placeholder="One point per line"
      onChange={(e) => onChange({ ...slide, points: e.target.value.split('\n') })}
      className="mb-2 text-sm"
      data-testid={`studio-slide-points-${slide.index}`}
    />
    {slide.scripture ? (
      <p className="text-xs text-amber-700" data-testid={`studio-slide-scripture-${slide.index}`}>
        {slide.scripture}
      </p>
    ) : null}
    <Textarea
      rows={2}
      value={slide.notes || ''}
      placeholder="Presenter notes"
      onChange={(e) => onChange({ ...slide, notes: e.target.value })}
      className="mt-2 text-xs text-slate-600"
      data-testid={`studio-slide-notes-${slide.index}`}
    />
  </div>
);

const PresentationStudio = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    source_type: 'free', tenant_id: '', lesson_id: '', event_id: '',
    title: '', body: '', template: 'fwm', audience: 'students',
    purpose: 'lecture', slide_count: 10,
  });
  const [current, setCurrent] = useState(null);

  const { data: meta } = useQuery({
    queryKey: ['studio-templates'],
    queryFn: () => api.get('/presentations/templates').then((r) => r.data),
  });
  const { data: status } = useQuery({
    queryKey: ['studio-status'],
    queryFn: () => api.get('/presentations/status').then((r) => r.data),
  });
  const { data: mine } = useQuery({
    queryKey: ['studio-mine'],
    queryFn: () => api.get('/presentations').then((r) => r.data),
  });
  const { data: schools } = useQuery({
    queryKey: ['studio-schools'],
    enabled: form.source_type === 'lesson',
    queryFn: () => api.get('/presentations/sources/schools').then((r) => r.data),
  });
  const { data: lessons } = useQuery({
    queryKey: ['studio-lessons', form.tenant_id],
    enabled: form.source_type === 'lesson' && !!form.tenant_id,
    queryFn: () => api.get('/presentations/sources/lessons',
      { params: { tenant_id: form.tenant_id } }).then((r) => r.data),
  });
  const { data: events } = useQuery({
    queryKey: ['studio-events'],
    enabled: form.source_type === 'event',
    queryFn: () => api.get('/presentations/sources/events').then((r) => r.data),
  });

  const draft = useMutation({
    mutationFn: () => api.post('/presentations/draft', form).then((r) => r.data),
    onSuccess: (d) => {
      setCurrent(d);
      toast.success(`${d.preview.length} slides drafted`);
      qc.invalidateQueries({ queryKey: ['studio-mine'] });
      qc.invalidateQueries({ queryKey: ['studio-status'] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not draft that deck'),
  });

  const save = useMutation({
    mutationFn: () => api.put(`/presentations/${current.id}`, {
      title: current.title,
      slides: current.preview.map((s) => ({
        kind: s.kind, title: s.title,
        points: (s.points || []).filter(Boolean),
        scripture: s.scripture, notes: s.notes, quote: s.title,
      })),
    }).then((r) => r.data),
    onSuccess: (d) => { setCurrent(d); toast.success('Saved'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not save'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/presentations/${id}`),
    onSuccess: () => {
      toast.success('Removed');
      setCurrent(null);
      qc.invalidateQueries({ queryKey: ['studio-mine'] });
    },
  });

  const download = async (id, title) => {
    try {
      const res = await fetch(`${API_URL}/api/presentations/${id}/download`,
        { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(title || 'presentation').replace(/[^\w -]/g, '')}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PowerPoint downloaded');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const templates = meta?.items || [];
  const canDraft = useMemo(() => {
    if (form.source_type === 'free') return form.body.trim().length >= 40;
    if (form.source_type === 'lesson') return form.tenant_id && form.lesson_id;
    return !!form.event_id;
  }, [form]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6" data-testid="presentation-studio">
      <a href="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"
         data-testid="studio-back">
        <ArrowLeft className="h-3 w-3" /> Back to the admin workspace
      </a>
      <div>
        <h1 className="flex items-center gap-2 font-serif text-2xl text-slate-900">
          <Presentation className="h-6 w-6 text-indigo-600" /> Presentation Studio
        </h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="studio-status-line">
          {status?.drafting || 'Loading…'}
          {status ? ` · ${status.drafts_today}/${status.daily_limit} today` : ''}
        </p>
        {status?.last_draft_fell_back && (
          <p className="mt-1 text-xs text-amber-700" data-testid="studio-fallback-note">
            Your last deck was cut from the source text instead — the AI draft
            did not come back. Check the review notes on it for the reason.
          </p>
        )}
        {status && !status.pdf_available && (
          <p className="mt-1 text-xs text-slate-400" data-testid="studio-pdf-note">
            {status.pdf_note}
          </p>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <Card data-testid="studio-form">
          <CardHeader className="pb-3"><CardTitle className="text-base">New presentation</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Source</Label>
              <Select value={form.source_type}
                      onValueChange={(v) => setForm({ ...form, source_type: v })}>
                <SelectTrigger data-testid="studio-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`studio-source-${s.key}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.source_type === 'lesson' && (
              <div className="grid gap-2">
                <Select value={form.tenant_id}
                        onValueChange={(v) => setForm({ ...form, tenant_id: v, lesson_id: '' })}>
                  <SelectTrigger className="w-full" data-testid="studio-school-picker">
                    <SelectValue placeholder="Choose the school" />
                  </SelectTrigger>
                  <SelectContent>
                    {(schools?.items || []).map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`studio-school-${s.id}`}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.lesson_id} disabled={!form.tenant_id}
                        onValueChange={(v) => setForm({ ...form, lesson_id: v })}>
                  <SelectTrigger className="w-full" data-testid="studio-lesson-picker">
                    <SelectValue placeholder={form.tenant_id ? 'Choose the lesson'
                                                             : 'Choose the school first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(lessons?.items || []).map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`studio-lesson-${l.id}`}>
                        {l.name}{l.thin ? ' — short lesson' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.tenant_id && (lessons?.items || []).length === 0 ? (
                  <p className="text-[11px] text-amber-600" data-testid="studio-no-lessons">
                    No lessons recorded at that school yet.
                  </p>
                ) : null}
                <p className="text-[11px] text-slate-400">
                  The deck is built from that lesson's own approved content — nothing
                  is added to it.
                </p>
              </div>
            )}
            {form.source_type === 'event' && (
              <Select value={form.event_id}
                      onValueChange={(v) => setForm({ ...form, event_id: v })}>
                <SelectTrigger className="w-full" data-testid="studio-event-picker">
                  <SelectValue placeholder="Choose the event" />
                </SelectTrigger>
                <SelectContent>
                  {(events?.items || []).map((ev) => (
                    <SelectItem key={ev.id} value={ev.id} data-testid={`studio-event-${ev.id}`}>
                      {ev.name}{ev.when ? ` · ${ev.when}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {form.source_type === 'free' && (
              <>
                <Input placeholder="Title" value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })}
                       data-testid="studio-title" />
                <Textarea rows={7} placeholder="Paste the approved material…"
                          value={form.body}
                          onChange={(e) => setForm({ ...form, body: e.target.value })}
                          data-testid="studio-body" />
              </>
            )}

            <div>
              <Label>Template</Label>
              <Select value={form.template}
                      onValueChange={(v) => setForm({ ...form, template: v })}>
                <SelectTrigger data-testid="studio-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key} data-testid={`studio-template-${t.key}`}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-slate-400">
                {templates.find((t) => t.key === form.template)?.description || ''}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Audience</Label>
                <Select value={form.audience}
                        onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="studio-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(meta?.audiences || {}).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Slides</Label>
                <Input type="number" min={3} max={40} value={form.slide_count}
                       onChange={(e) => setForm({ ...form, slide_count: Number(e.target.value) })}
                       data-testid="studio-slide-count" />
              </div>
            </div>

            <div>
              <Label>Purpose</Label>
              <Select value={form.purpose}
                      onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="studio-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(meta?.purposes || {}).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" disabled={!canDraft || draft.isPending}
                    onClick={() => draft.mutate()} data-testid="studio-generate">
              {draft.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>
                : <><Sparkles className="mr-2 h-4 w-4" /> Generate preview</>}
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {current ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900" data-testid="studio-current-title">
                    {current.title}
                  </h2>
                  <p className="text-[11px] text-slate-400">
                    {current.preview.length} slides · drafted by {current.drafted_by}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" disabled={save.isPending}
                          onClick={() => save.mutate()} data-testid="studio-save">
                    <Save className="mr-1 h-4 w-4" /> Save edits
                  </Button>
                  <Button onClick={() => download(current.id, current.title)}
                          data-testid="studio-download">
                    <Download className="mr-1 h-4 w-4" /> PowerPoint
                  </Button>
                </div>
              </div>

              {(current.gaps || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                     data-testid="studio-gaps">
                  <p className="flex items-center gap-1 text-xs font-semibold text-amber-900">
                    <AlertTriangle className="h-3.5 w-3.5" /> For your review
                  </p>
                  <ul className="mt-1 list-disc pl-5 text-xs text-amber-900">
                    {current.gaps.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-2">
                {current.preview.map((slide) => (
                  <SlidePreview
                    key={slide.index}
                    slide={slide}
                    onChange={(next) => setCurrent({
                      ...current,
                      preview: current.preview.map((s) => (s.index === next.index ? next : s)),
                    })}
                  />
                ))}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="p-10 text-center text-sm text-slate-500"
                           data-testid="studio-empty">
                <FileText className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                Choose a source and generate a preview. Nothing is downloaded until
                you have read it.
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Your presentations</CardTitle></CardHeader>
            <CardContent className="divide-y divide-slate-100 p-0" data-testid="studio-list">
              {(mine?.items || []).length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-400">None yet.</p>
              ) : mine.items.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-2 p-3"
                     data-testid={`studio-item-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{p.title}</p>
                    <p className="text-[11px] text-slate-400">
                      {p.source?.type} · {p.preview?.length || 0} slides · {p.template}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setCurrent(p)}
                          data-testid={`studio-open-${p.id}`}>Open</Button>
                  <Button size="sm" variant="ghost" onClick={() => download(p.id, p.title)}
                          data-testid={`studio-download-${p.id}`}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-600"
                          onClick={() => remove.mutate(p.id)}
                          data-testid={`studio-delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PresentationStudio;
